/**
 * TerminalAgentControlToggle — hand the agent durable control of this terminal.
 *
 * Toggling on grants the canvas agent read/write access to this widget's tmux session
 * (Tool_CanvasTerminal). The grant is canvas-scoped server state that persists across
 * messages until revoked; we also mirror it into node data for instant, reload-safe UI.
 * When the agent is actively driving the session a pulse is shown (see useAgentTerminalActivity).
 */
import { orgAPI } from "@/api/api";
import { cn } from "@/utils";
import { Bot } from "lucide-react";
import { useState } from "react";
import { useCanvasApi } from "../../../context/CanvasContext";
import { useCanvasRoute } from "../../../context/CanvasRouteContext";
import { useAgentTerminalActivity } from "./useAgentTerminalActivity";

interface Props {
  id: string;
  data: Record<string, unknown>;
}

export function TerminalAgentControlToggle({ id, data }: Props) {
  const canvas = useCanvasApi();
  const { canEdit } = canvas;
  const { canvasId } = useCanvasRoute();

  const enabled = Boolean(data.agentControlEnabled);
  const sessionId =
    typeof data.terminalSessionId === "string" ? data.terminalSessionId : "";
  const active = useAgentTerminalActivity(sessionId);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (!canEdit || !canvasId || !sessionId || busy) return;
    const next = !enabled;
    setBusy(true);
    // Optimistic: drive the UI from node data immediately; the POST keeps the
    // server-side grant (which the agent actually reads) in sync.
    canvas.updateNodeData(id, { agentControlEnabled: next });
    try {
      await orgAPI.canvas.setSandboxTerminalAgentControl(canvasId, {
        sessionId,
        enabled: next,
      });
    } catch (err) {
      canvas.updateNodeData(id, { agentControlEnabled: enabled }); // revert
      console.error("[Terminal] failed to update agent control", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      title={
        enabled
          ? "Agent control is ON — the agent can read and type in this terminal. Click to revoke."
          : "Agent control is OFF. Click to let the agent read and type in this terminal."
      }
      aria-label="Toggle agent control of this terminal"
      aria-pressed={enabled}
      disabled={!canEdit || !sessionId || busy}
      onClick={(e) => {
        e.stopPropagation();
        void toggle();
      }}
      className={cn(
        "relative h-7 px-2 flex items-center gap-1 rounded-lg text-xs font-medium transition-colors focus:outline-none disabled:opacity-50",
        enabled
          ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/25"
          : "text-muted-foreground hover:text-foreground hover:bg-black/8 dark:hover:bg-white/10",
      )}
    >
      <Bot className="h-[15px] w-[15px]" />
      <span>Agent</span>
      {active && (
        // Live pulse while the agent is reading/typing in this session.
        <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
      )}
    </button>
  );
}
